<template>
  <section class="scene-helpers" v-if="options">

    <div class="button helper action options"
         @click="isHelpersShowed = !isHelpersShowed"
         @mouseover="tooltipOptions = {
           content: 'Toggle helpers',
           styles: { top: '20px', right: '0px' }
         }"
         @mouseleave="tooltipReset()">
      <svg class="icon">
        <transition name="fade">
          <use xlink:href="#helper-settings" v-if="!isHelpersShowed"></use>
        </transition>
        <transition name="fade">
          <use xlink:href="#helper-settings-active" v-if="isHelpersShowed"></use>
        </transition>
      </svg>
    </div>

    <transition name="fade">
      <div class="button action introduction" v-if="isHelpersShowed"
           @click="onIntroductionsClick"
           @mouseover="tooltipOptions = {
             content: 'Show introduction',
             styles: { top: '78px', right: '-14px' }
           }"
           @mouseleave="tooltipReset()">
        <svg class="icon">
          <use xlink:href="#helper-info"></use>
        </svg>
      </div>
    </transition>

    <transition name="fade">
      <div class="button action highlight" :class="{ 'active': isHighlighted }" v-if="isHelpersShowed"
           @click="onToggleHighlight"
           @mouseover="tooltipOptions = {
             content: 'Highlight scene objects',
             styles: { top: '138px', right: '-28px' }
           }"
           @mouseleave="tooltipReset()">
        <div class="decoration-light"></div>
        <svg class="icon">
          <use xlink:href="#helper-highlight" v-if="!isHighlighted"></use>
          <use xlink:href="#helper-highlight-active" v-if="isHighlighted"></use>
        </svg>
      </div>
    </transition>

    <transition name="fade">
      <div class="button action daytime"
           :class="{ 'active': isDay }"
           v-if="isHelpersShowed"
           @click="onToggleDayNight"
           @mouseover="tooltipOptions = {
             content: 'Day/Night switch',
             styles: { top: '268px', right: '0px' }
           }"
           @mouseleave="tooltipReset()">
        <div class="decoration-light"></div>
        <svg class="icon">
          <use xlink:href="#sun-zenith" v-if="!isDay"></use>
          <use xlink:href="#sun-zenith" v-if="isDay"></use>
        </svg>
      </div>
    </transition>

    <transition name="fade">
      <div class="button action rotation"
           :class="{ 'active': options.autoRotate.enabled && !options.autoRotate.paused }"
           v-if="isHelpersShowed"
           @click="onToggleAutoRotate"
           @mouseover="tooltipOptions = {
             content: 'Toggle scene autorotate',
             styles: { top: '204px', right: '-32px' }
           }"
           @mouseleave="tooltipReset()">
        <div class="decoration-light"></div>
        <svg class="icon">
          <transition name="fade">
            <use xlink:href="#helper-rotation" v-if="!options.autoRotate.enabled"></use>
          </transition>
          <transition name="fade">
            <use xlink:href="#helper-rotation-active" v-if="options.autoRotate.enabled"></use>
          </transition>
        </svg>
      </div>
    </transition>

    <tooltip v-if="tooltipOptions" :options="tooltipOptions"/>

  </section>
</template>

<script>
  import Tooltip from '../common/Tooltip'

  export default {
    name: 'SceneHelpers',

    components: { Tooltip },

    props: {
      options: {
        type: Object,
        default: () => {
          return {}
        }
      }
    },

    data() {
      return {
        isInstructionsShowed: true,
        isHelpersShowed: true,
        tooltipOptions: {
          content: '',
          styles: {}
        },
        isHighlighted: false,
        isDay: true
      }
    },

    methods: {
      onIntroductionsClick() {
        this.$emit('introductionsShow')
      },

      onToggleHighlight() {
        this.isHighlighted = !this.isHighlighted
        this.$emit('toggleHighlight', this.isHighlighted)
      },

      onToggleDayNight() {
        this.isDay = !this.isDay
        this.$emit('toggleDayNight', this.isDay)
      },

      onToggleAutoRotate() {
        this.$emit('toggleAutoRotate')
      },

      tooltipReset() {
        this.tooltipOptions = { content: '', styles: {} }
      }
    },

    watch: {
      'options.instructions.show': function (value) {
        if (!value) {

        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../assets/styles/mixins";

  .scene-helpers {
    z-index: 1;
  }

  .button {
    position: absolute;
    font-size: 22px;
    line-height: 24px;
    font-weight: 600;
    @include border-radius(50%);
    width: 30px;
    height: 30px;
    cursor: pointer;
    @include box-shadow(0 0 20px 4px rgba(0, 0, 0, 0.1));
    background: radial-gradient(rgba(0, 0, 0, 0.12) 50%, rgba(125, 125, 125, 0.1) 100%);

    .decoration-light {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      @include border-radius(50%);
      background: radial-gradient(rgba(255, 255, 255, .85) 35%, rgba(255, 255, 255, .15) 100%);
      opacity: 0;
      @include box-shadow(0 0 10px 4px rgba(255, 255, 255, .5));
      @include transition(all .25s, box-shadow .25s ease-in-out, opacity .25s);
    }

    &.active {
      .decoration-light {
        opacity: 1;
      }
    }

    .icon {
      width: 24px;
      height: 24px;
      position: absolute;
      top: 3px;
      left: 3px;
    }

    &.options {
      top: 25px;
      right: 20px;
    }

    &.introduction {
      top: 85px;
      right: 20px;
    }

    &.highlight {
      top: 145px;
      right: 20px;
    }

    &.rotation {
      top: 210px;
      right: 20px;

      &.active {
        .decoration-light {
          opacity: .35;
        }
      }
    }

    &.daytime {
      top: 275px;
      right: 20px;

      .icon {
        color: #ffffff;
      }
    }

    .toggle-enter-active,
    .toggle-leave-active {
      @include transition(all .35s);
    }

    .toggle-enter,
    .toggle-leave-to {
      opacity: 0;
      @include transform-translate(0, -100px);
    }
  }
</style>
